/**
 * App-style Quick Add: size tiles, colour switch, slim add-to-cart.
 * Content is loaded from ?view=quick-add (see templates/product.quick-add.liquid).
 */
(() => {
  const quickAddUrl = (href) => {
    const url = new URL(href, window.location.origin);
    url.searchParams.set("view", "quick-add");
    return url.toString();
  };

  const parseJson = (node, fallback) => {
    if (!node) return fallback;
    try {
      return JSON.parse(node.textContent);
    } catch (error) {
      return fallback;
    }
  };

  const moneyText = (value) =>
    String(value ?? "")
      .replace(/<[^>]+>/g, "")
      .replace(/&nbsp;/gi, " ")
      .trim();

  class QuickAddSheet extends HTMLElement {
    connectedCallback() {
      this.form = this.querySelector("form");
      this.variantInput = this.querySelector("[data-quick-add-id]");
      this.cta = this.querySelector("[data-quick-add-cta]");
      this.thumb = this.querySelector(".quick-add__thumb");
      this.price = this.querySelector("[data-quick-add-price]");
      this.compare = this.querySelector("[data-quick-add-compare]");
      this.saleNotice = this.querySelector("[data-quick-add-sale]");
      this.guide = this.querySelector("[data-quick-add-guide]");
      this.guideBody = this.querySelector("[data-quick-add-guide-body]");
      this.colorLabel = this.querySelector("[data-quick-add-color-label]");
      this.variants = parseJson(this.querySelector("[data-quick-add-variants]"), []);
      this.meta = parseJson(this.querySelector("[data-quick-add-options]"), {
        colorNames: [],
        sizeNames: [],
        colorTemplate: "Farbe: __VALUE__",
      });
      this.hasLinkedColors = this.getAttribute("data-has-linked-colors") === "true";
      this.labels = {
        chooseSize: this.getAttribute("data-choose-size") || "Größe wählen",
        add: this.getAttribute("data-add-label") || "Hinzufügen",
        soldOut: this.getAttribute("data-sold-out") || "Ausverkauft",
        added: this.getAttribute("data-added-label") || "✓",
      };
      this.selected = {};
      this.justAdded = false;

      this.addEventListener("click", this.onClick);
      this.form?.addEventListener("submit", this.onSubmit);
      this.form?.addEventListener("variant:added", this.onAdded);
      document.documentElement.addEventListener("cart:updated", this.onCartUpdated);
      this.seedSelection();
      this.sync();
      if (window.semodeWishlist && typeof window.semodeWishlist.paint === 'function') {
        window.semodeWishlist.paint();
      }
    }

    disconnectedCallback() {
      this.removeEventListener("click", this.onClick);
      this.form?.removeEventListener("submit", this.onSubmit);
      this.form?.removeEventListener("variant:added", this.onAdded);
      document.documentElement.removeEventListener("cart:updated", this.onCartUpdated);
      clearTimeout(this.addedTimer);
    }

    onSubmit = (event) => {
      if (!this.variantInput?.value) {
        event.preventDefault();
        event.stopImmediatePropagation();
      }
    };

    onAdded = () => {
      if (this.getAttribute("data-surface") === "pdp") {
        this.closeHost();
        return;
      }
      this.justAdded = true;
      this.sync();
      clearTimeout(this.addedTimer);
      this.addedTimer = setTimeout(() => {
        this.justAdded = false;
        this.sync();
      }, 1200);
    };

    onCartUpdated = () => {
      this.sync();
    };

    setCtaLabel(text) {
      if (!this.cta) return;
      const label = this.cta.querySelector(".loader-button__text");
      if (label) label.textContent = text;
      else this.cta.textContent = text;
    }

    euBlocked() {
      if (this.cta?.hasAttribute("data-eu-limit-blocked")) return true;
      const category = this.getAttribute("data-eu-product-category");
      const entry = category && window.EUCartLimitConfig?.categories?.[category];
      return Boolean(entry && this.cta?.getAttribute("data-eu-limit-blocked") === "true");
    }

    euMessage() {
      const category = this.getAttribute("data-eu-product-category");
      return window.EUCartLimitConfig?.categories?.[category]?.msg || "";
    }

    onClick = (event) => {
      if (event.target.closest("[data-quick-add-guide-close]")) {
        event.preventDefault();
        if (this.guide) this.guide.hidden = true;
        return;
      }

      if (event.target.closest("[data-quick-add-guide-open]")) {
        event.preventDefault();
        this.openGuide();
        return;
      }

      const productButton = event.target.closest("[data-quick-add-product-url]");
      if (productButton) {
        event.preventDefault();
        const nextUrl = productButton.getAttribute("data-quick-add-product-url");
        if (nextUrl && !productButton.classList.contains("is-selected")) {
          this.previewLinkedSwatch(productButton);
          this.switchProduct(nextUrl);
        }
        return;
      }

      const optionButton = event.target.closest("[data-quick-add-option]");
      if (!optionButton) return;
      event.preventDefault();
      this.selected[optionButton.getAttribute("data-quick-add-option")] = optionButton.getAttribute("data-quick-add-value");
      this.sync();
    };

    closeHost() {
      const host = this.closest("quick-buy-popover, quick-buy-drawer, popover-content");
      if (host) host.open = false;
    }

    openExistingSizeModal() {
      const modal = [...document.querySelectorAll(".sc-modal")].find((el) => {
        if (el.closest(".hidden-pocket")) return false;
        const parent = el.parentElement;
        return !parent || getComputedStyle(parent).display !== "none";
      });
      if (!modal) return false;
      this.closeHost();
      modal.setAttribute("open", "");
      modal.removeAttribute("aria-hidden");
      document.documentElement.classList.add("sc-modal-lock");
      return true;
    }

    extractGuide(doc) {
      return (
        doc.querySelector(".sc-page-wrap") ||
        doc.querySelector("[id*='size_chart'] .shopify-section") ||
        doc.querySelector("[id*='size_chart']") ||
        doc.querySelector("[data-sizecalc]")?.closest(".shopify-section") ||
        doc.querySelector(".sizecalc-table")?.closest(".shopify-section") ||
        null
      );
    }

    async openGuide() {
      if (this.openExistingSizeModal()) return;
      if (!this.guide) return;

      this.guide.hidden = false;
      if (!this.guideBody || this.guideBody.dataset.loaded === "true") return;

      const pageUrl = this.getAttribute("data-size-guide-url") || "/pages/grossentabelle";
      const sectionUrl = pageUrl.includes("?")
        ? `${pageUrl}&section_id=size_chart`
        : `${pageUrl}?section_id=size_chart`;

      try {
        let response = await fetch(sectionUrl);
        let html = await response.text();
        let doc = new DOMParser().parseFromString(html, "text/html");
        let source = this.extractGuide(doc);

        if (!source) {
          response = await fetch(pageUrl);
          html = await response.text();
          doc = new DOMParser().parseFromString(html, "text/html");
          source = this.extractGuide(doc);
        }

        if (!source) {
          window.location.href = pageUrl;
          return;
        }

        this.guideBody.innerHTML = "";
        this.guideBody.appendChild(document.importNode(source, true));
        this.guideBody.dataset.loaded = "true";
      } catch (error) {
        window.location.href = pageUrl;
      }
    }

    isColorName(name) {
      return this.meta.colorNames.includes(String(name || "").toLowerCase());
    }

    previewLinkedSwatch(button) {
      this.querySelectorAll("[data-quick-add-product-url]").forEach((el) => {
        const selected = el === button;
        el.classList.toggle("is-selected", selected);
        el.setAttribute("aria-pressed", selected ? "true" : "false");
      });
      const img = button.querySelector("img");
      if (img && this.thumb) this.thumb.src = img.currentSrc || img.src;
      const label = button.getAttribute("aria-label") || "";
      const title = this.querySelector(".quick-add__title");
      if (title && label) title.textContent = label;
      if (this.colorLabel && label) {
        this.colorLabel.textContent = this.meta.colorTemplate.replace("__VALUE__", label);
      }
    }

    seedSelection() {
      if (this.variants.length === 1) {
        Object.assign(this.selected, this.variants[0].options || {});
        return;
      }

      const first = this.variants[0];
      if (!first) return;

      Object.keys(first.options || {}).forEach((name) => {
        if (this.hasLinkedColors && this.isColorName(name)) return;
        if (this.isColorName(name)) {
          const marked = this.querySelector(
            `.quick-add__swatch.is-selected[data-quick-add-option="${CSS.escape(name)}"]`
          );
          this.selected[name] = marked?.getAttribute("data-quick-add-value") || first.options[name];
        }
      });
    }

    currentVariant() {
      if (this.variants.length === 1) return this.variants[0];

      return this.variants.find((variant) =>
        Object.entries(this.selected).every(([name, value]) => variant.options[name] === value)
      );
    }

    requiredNames() {
      const names = new Set();
      this.variants.forEach((variant) => {
        Object.keys(variant.options || {}).forEach((name) => {
          if (name === "Title" && variant.options[name] === "Default Title") return;
          if (this.hasLinkedColors && this.isColorName(name)) return;
          names.add(name);
        });
      });
      return [...names];
    }

    optionAvailable(name, value) {
      return this.variants.some((variant) => {
        if (variant.options[name] !== value) return false;
        const matchesOthers = Object.entries(this.selected).every(
          ([selectedName, selectedValue]) => selectedName === name || variant.options[selectedName] === selectedValue
        );
        return matchesOthers && variant.available;
      });
    }

    sync() {
      const required = this.requiredNames();
      const allChosen = required.every((name) => this.selected[name]);
      const variant = allChosen ? this.currentVariant() : undefined;
      const canAdd = Boolean(variant && variant.available);

      this.querySelectorAll("[data-quick-add-option]").forEach((button) => {
        const name = button.getAttribute("data-quick-add-option");
        const value = button.getAttribute("data-quick-add-value");
        const selected = this.selected[name] === value;
        button.classList.toggle("is-selected", selected);
        button.setAttribute("aria-pressed", selected ? "true" : "false");
        const available = this.optionAvailable(name, value);
        button.classList.toggle("is-unavailable", !available);
        if (button.classList.contains("quick-add__tile")) {
          button.setAttribute("aria-label", available ? value : `${value}, ${this.labels.soldOut}`);
        }
      });

      if (variant?.image && this.thumb) this.thumb.src = variant.image;

      if (variant && this.price) {
        this.price.textContent = moneyText(variant.priceLabel);
        this.price.classList.toggle("quick-add__price--sale", Boolean(variant.onSale && variant.compare));
      }

      if (variant && this.compare) {
        if (variant.compare) {
          this.compare.textContent = moneyText(variant.compareLabel);
          this.compare.hidden = false;
        } else {
          this.compare.textContent = "";
          this.compare.hidden = true;
        }
      }

      if (this.saleNotice && variant) {
        this.saleNotice.hidden = !variant.onSale;
      }

      if (this.colorLabel) {
        const colorName = required.find((name) => this.isColorName(name)) || Object.keys(this.selected).find((name) => this.isColorName(name));
        const colorValue = colorName ? this.selected[colorName] : "";
        if (colorValue) {
          this.colorLabel.textContent = this.meta.colorTemplate.replace("__VALUE__", colorValue);
        }
      }

      if (this.variantInput) {
        this.variantInput.disabled = !canAdd;
        this.variantInput.value = canAdd ? String(variant.id) : "";
      }

      if (this.cta) {
        const blocked = canAdd && this.cta.hasAttribute("data-eu-limit-blocked");
        this.cta.disabled = !canAdd || blocked;
        this.cta.classList.toggle("is-pick", !allChosen);
        this.cta.classList.toggle("is-soldout", Boolean(allChosen && (!variant || !variant.available)));
        this.cta.classList.toggle("is-ready", canAdd && !blocked && !this.justAdded);
        this.cta.classList.toggle("is-blocked", blocked);
        if (this.justAdded) {
          this.setCtaLabel(this.labels.added);
        } else if (!allChosen) {
          this.setCtaLabel(this.labels.chooseSize);
        } else if (!variant || !variant.available) {
          this.setCtaLabel(this.labels.soldOut);
        } else if (blocked) {
          this.setCtaLabel(this.euMessage() || this.labels.soldOut);
        } else {
          this.setCtaLabel(this.labels.add);
        }
      }
    }

    async switchProduct(href) {
      if (this.getAttribute("data-surface") === "pdp") {
        this.closeHost();
        window.location.assign(new URL(href, window.location.origin).toString());
        return;
      }
      const host = this.closest("quick-buy-popover, quick-buy-drawer");
      if (!host) return;

      host.setAttribute("aria-busy", "true");
      try {
        const response = await fetch(quickAddUrl(href));
        const html = document.createRange().createContextualFragment(await response.text());
        const source = html.querySelector("#quick-buy-content");
        const next = source?.content.querySelector(host.tagName.toLowerCase());
        if (!next) return;
        host.replaceChildren(...next.children);
      } finally {
        host.removeAttribute("aria-busy");
      }
    }
  }

  if (!window.customElements.get("quick-add-sheet")) {
    window.customElements.define("quick-add-sheet", QuickAddSheet);
  }

  window.themeQuickAddUrl = quickAddUrl;

  const hoistCartQuickAdd = () => {
    const incoming = document.querySelectorAll("#mini-cart quick-buy-popover");
    if (!incoming.length) return;

    document.querySelectorAll("body > quick-buy-popover[data-qa-hoisted]").forEach((popover) => {
      popover.remove();
    });

    incoming.forEach((popover) => {
      popover.setAttribute("data-qa-hoisted", "");
      document.body.appendChild(popover);
    });
  };

  const watchCart = () => {
    const cart = document.querySelector("#mini-cart");
    if (!cart || cart.dataset.qaHoistWatch === "true") return;
    cart.dataset.qaHoistWatch = "true";
    new MutationObserver(() => hoistCartQuickAdd()).observe(cart, {
      childList: true,
      subtree: true,
    });
  };

  const start = () => {
    hoistCartQuickAdd();
    watchCart();
  };

  if (document.readyState === "loading") {
    document.addEventListener("DOMContentLoaded", start, { once: true });
  } else {
    start();
  }

  document.addEventListener("cart:updated", () => {
    requestAnimationFrame(start);
  });

  document.addEventListener("shopify:section:load", (event) => {
    if (event.target?.id === "shopify-section-mini-cart" || event.target?.querySelector?.("#mini-cart")) {
      start();
    }
  });
})();
